import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  Divider,
  InputAdornment,
  InputLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { TypographyProps } from '@mui/system';
import { PaymentMethod } from '@tyro/api';
import { RHFTextField, usePreferredNameLayout } from '@tyro/core';
import { Trans, useFormatNumber, useTranslation } from '@tyro/i18n';
import dayjs from 'dayjs';
import { useId, useMemo, useState } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { useServiceCharge } from '../../../api/service-charge';
import type { ReturnTypeFromUseStudentFees } from '../../../api/student-fees';
import type { PayFeesFormValues } from './types';

interface PaymentDetailsProps {
  control: Control<PayFeesFormValues>;
}

function PaymentName({
  feeName,
  person,
  ...props
}: Pick<PayFeesFormValues['feesById'][number], 'feeName' | 'person'> &
  Partial<TypographyProps>) {
  const { displayName } = usePreferredNameLayout();
  const studentName = displayName(person);

  return (
    <Typography
      variant="subtitle1"
      component="span"
      sx={{
        lineHeight: 1.2,
      }}
      {...props}
    >
      <Trans ns="fees" i18nKey="feeNameForStudent">
        {{ feeName }}
        <br />
        <Typography variant="body2" component="span" color="text.secondary">
          <>for {{ studentName }}</>
        </Typography>
      </Trans>
    </Typography>
  );
}

type InstalmentDetailProps = {
  instalment: ReturnTypeFromUseStudentFees['paymentPlanInstallments'][number];
};

function InstalmentDetail({ instalment }: InstalmentDetailProps) {
  const { t } = useTranslation(['common', 'fees']);
  const { formatCurrency } = useFormatNumber();

  let detailText = '';
  const formattedFeeAmount = formatCurrency(instalment.amount);

  if (instalment.installment === 1) {
    detailText = t('fees:planTodaysFee', { feeAmount: formattedFeeAmount });
  } else {
    const formattedDate = dayjs(instalment.installmentDate).format(
      'DD/MM/YYYY',
    );

    detailText = t('fees:feeOnXDate', {
      feeAmount: formattedFeeAmount,
      date: formattedDate,
    });
  }

  return (
    <Typography variant="body2" component="span" color="text.secondary">
      {detailText}
    </Typography>
  );
}

export function PaymentDetailsForPayInFull({ control }: PaymentDetailsProps) {
  const componentId = useId();
  const { formatCurrency } = useFormatNumber();
  const { t } = useTranslation(['common', 'fees']);

  const { paymentMethod, feesById } = useWatch({
    control,
  });

  const fees = Object.values(feesById || {}).flatMap((fee) =>
    fee ? [fee] : [],
  ) as PayFeesFormValues['feesById'][number][];

  const { data: serviceCharge } = useServiceCharge({
    charges: fees!.map(({ id, amountToPay }) => ({
      feeId: id!.feeId!,
      amount: amountToPay!,
    })),
  });

  const total = useMemo(
    () => fees!.reduce((acc, fee) => acc + Number(fee!.amountToPay ?? 0), 0),
    [JSON.stringify(fees)],
  );

  const descriptionId = `${componentId}-description`;
  const totalId = `${componentId}-total`;

  return (
    <Stack
      spacing={2}
      sx={{
        backgroundColor: 'slate.100',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="subtitle1" color="slate.500" id={descriptionId}>
          {t('common:description')}
        </Typography>
        <Typography variant="subtitle1" color="slate.500" id={totalId}>
          {t('common:total')}
        </Typography>
      </Stack>
      <Stack spacing={2}>
        {fees.map((fee) => {
          const { feeName, person, id } = fee;

          return (
            <Stack
              key={`${id.feeId}-${id.studentPartyId}`}
              direction="row"
              justifyContent="space-between"
            >
              <PaymentName
                feeName={feeName}
                person={person}
                aria-labelledby={descriptionId}
              />

              <Stack
                alignItems="flex-end"
                spacing={1}
                aria-labelledby={totalId}
              >
                <Typography variant="subtitle1" component="p">
                  {formatCurrency(fee.amountDue ?? 0)}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      <Divider sx={{ borderColor: 'slate.200' }} />
      <Stack alignItems="flex-end">
        <Typography variant="subtitle1">
          {t('fees:total')} {formatCurrency(total)}
        </Typography>
        <Collapse in={paymentMethod === PaymentMethod.Card}>
          <Typography variant="body1">
            {t('fees:serviceFee')}{' '}
            {formatCurrency(
              (serviceCharge?.userServiceCharge ?? 0) +
                (serviceCharge?.userVat ?? 0),
            )}
          </Typography>
        </Collapse>
      </Stack>
    </Stack>
  );
}

export function PaymentDetailsForPartialPayment({
  control,
}: PaymentDetailsProps) {
  const componentId = useId();
  const { formatCurrency } = useFormatNumber();
  const { t } = useTranslation(['common', 'fees']);

  const { paymentMethod, feesById } = useWatch({
    control,
  });

  const fees = Object.values(feesById || {}).flatMap((fee) =>
    fee ? [fee] : [],
  ) as PayFeesFormValues['feesById'][number][];

  const { data: serviceCharge } = useServiceCharge({
    charges: fees.map(({ id, amountToPay }) => ({
      feeId: id!.feeId!,
      amount: amountToPay!,
    })),
  });

  const { total, eligiblePartialFees, otherFees } = useMemo(
    () => ({
      total: fees!.reduce((acc, fee) => acc + Number(fee.amountToPay ?? 0), 0),
      eligiblePartialFees: fees.filter((fee) => fee.allowPartialPayments),
      otherFees: fees.filter((fee) => !fee.allowPartialPayments),
    }),
    [JSON.stringify(fees)],
  );

  const descriptionId = `${componentId}-description`;
  const totalId = `${componentId}-total`;

  const hasOtherFees = otherFees.length > 0;

  return (
    <Stack spacing={3}>
      {hasOtherFees && (
        <Alert severity="warning">
          <AlertTitle>{t('common:warning')}</AlertTitle>
          {t('fees:ineligiblePartialPaymentWarning')}
        </Alert>
      )}
      <Stack spacing={2}>
        <Typography variant="subtitle1" color="slate.500">
          {t('fees:partialPaymentFees')}
        </Typography>
        <Stack
          spacing={2}
          sx={{
            backgroundColor: 'slate.100',
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography
              variant="subtitle1"
              color="slate.500"
              id={`${descriptionId}-partial`}
            >
              {t('common:description')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="slate.500"
              id={`${totalId}-partial`}
            >
              {t('common:total')}
            </Typography>
          </Stack>
          <Stack spacing={2}>
            {eligiblePartialFees.map((fee, index) => {
              const { feeName, person, id } = fee;
              const partialInputId = `${componentId}-partial-${index}`;
              const keyFee = `${id.studentPartyId}${id.feeId}`;

              return (
                <Stack
                  key={keyFee}
                  direction="row"
                  justifyContent="space-between"
                >
                  <PaymentName
                    feeName={feeName}
                    person={person}
                    aria-labelledby={`${descriptionId}-partial`}
                  />

                  <Stack
                    alignItems="flex-end"
                    spacing={1}
                    aria-labelledby={`${totalId}-partial`}
                  >
                    <Typography variant="subtitle1" component="p">
                      {formatCurrency(fee.amountDue ?? 0)}
                    </Typography>
                    <Tooltip
                      title={
                        fee.allowPartialPayments
                          ? undefined
                          : t('fees:partialPaymentsNotAllowed', { count: 1 })
                      }
                      arrow
                      placement="top"
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <InputLabel htmlFor={partialInputId}>
                          {t('fees:partial')}
                        </InputLabel>
                        <RHFTextField
                          controlProps={{
                            name: `feesById.${keyFee}.amountToPay`,
                            control,
                          }}
                          textFieldProps={{
                            id: partialInputId,
                            disabled: !fee.allowPartialPayments,
                            sx: {
                              width: 100,
                              '& .MuiInputBase-root': {
                                backgroundColor: 'white',
                              },
                            },
                            size: 'small',
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  €
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Stack>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      {hasOtherFees && (
        <Stack spacing={2}>
          <Typography variant="subtitle1" color="slate.500">
            {t('fees:fullPaymentFees')}
          </Typography>
          <Stack
            spacing={2}
            sx={{
              backgroundColor: 'slate.100',
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant="subtitle1"
                color="slate.500"
                id={`${descriptionId}-full`}
              >
                {t('common:description')}
              </Typography>
              <Typography
                variant="subtitle1"
                color="slate.500"
                id={`${totalId}-full`}
              >
                {t('common:total')}
              </Typography>
            </Stack>
            <Stack spacing={2}>
              {otherFees.map((fee) => {
                const { feeName, person, id } = fee;

                return (
                  <Stack
                    key={`${id.feeId}-${id.studentPartyId}`}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <PaymentName
                      feeName={feeName}
                      person={person}
                      aria-labelledby={`${descriptionId}-full`}
                    />

                    <Stack
                      alignItems="flex-end"
                      spacing={1}
                      aria-labelledby={`${totalId}-full`}
                    >
                      <Typography variant="subtitle1" component="p">
                        {formatCurrency(fee.amountDue ?? 0)}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      )}
      <Stack
        spacing={2}
        sx={{
          backgroundColor: 'slate.100',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Stack alignItems="flex-end">
          <Typography variant="subtitle1">
            {t('fees:total')} {formatCurrency(total)}
          </Typography>
          <Collapse in={paymentMethod === PaymentMethod.Card}>
            <Typography variant="body1">
              {t('fees:serviceFee')}{' '}
              {formatCurrency(
                (serviceCharge?.userServiceCharge ?? 0) +
                  (serviceCharge?.userVat ?? 0),
              )}
            </Typography>
          </Collapse>
        </Stack>
      </Stack>
    </Stack>
  );
}

export function PaymentDetailsForPaymentPlan({ control }: PaymentDetailsProps) {
  const componentId = useId();
  const { formatCurrency } = useFormatNumber();
  const { t } = useTranslation(['common', 'fees']);
  const [showInstallments, setShowInstallments] = useState<boolean[]>([]);

  const { feesById = {} } = useWatch({
    control,
  });

  const fees = Object.values(feesById).flatMap((fee) =>
    fee ? [fee] : [],
  ) as PayFeesFormValues['feesById'][number][];

  const {
    todaysTotal,
    todaysServiceCharges,
    eligiblePaymentPlanFees,
    otherFees,
  } = useMemo(() => {
    const eligibleFees = fees.filter((fee) => fee.paymentPlan);

    return {
      todaysTotal: eligibleFees.reduce(
        (acc, fee) =>
          acc + Number(fee.paymentPlanInstallments?.[0]?.amount ?? 0),
        0,
      ),
      todaysServiceCharges: eligibleFees.reduce(
        (acc, fee) =>
          acc + Number(fee.paymentPlanInstallments?.[0]?.serviceCharges ?? 0),
        0,
      ),
      eligiblePaymentPlanFees: eligibleFees,
      otherFees: fees.filter((fee) => !fee.paymentPlan),
    };
  }, [JSON.stringify(fees)]);

  const descriptionId = `${componentId}-description`;
  const totalId = `${componentId}-total`;

  const hasOtherFees = otherFees.length > 0;

  return (
    <Stack spacing={3}>
      {hasOtherFees && (
        <Alert severity="warning">
          <AlertTitle>{t('common:warning')}</AlertTitle>
          {t('fees:ineligiblePaymentPlanWarning')}
        </Alert>
      )}
      <Stack spacing={2}>
        <Typography variant="subtitle1" color="slate.500">
          {t('fees:paymentPlanFees')}
        </Typography>
        <Stack
          spacing={2}
          sx={{
            backgroundColor: 'slate.100',
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography
              variant="subtitle1"
              color="slate.500"
              id={`${descriptionId}-plan`}
            >
              {t('common:description')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="slate.500"
              id={`${totalId}-plan`}
            >
              {t('common:details')}
            </Typography>
          </Stack>
          <Stack spacing={2}>
            {eligiblePaymentPlanFees.map((fee, index) => {
              const { feeName, person, paymentPlanInstallments, id } = fee;
              const serviceFee =
                paymentPlanInstallments?.reduce(
                  (acc, { serviceCharges }) => acc + (serviceCharges ?? 0),
                  0,
                ) ?? 0;
              const showDetails = showInstallments[index];
              const [firstInstallment, ...restInstallments] =
                paymentPlanInstallments || [];

              return (
                <Stack
                  key={`${id.feeId}-${id.studentPartyId}`}
                  direction="row"
                  justifyContent="space-between"
                >
                  <PaymentName
                    feeName={feeName}
                    person={person}
                    aria-labelledby={`${descriptionId}-plan`}
                  />
                  <Stack alignItems="flex-end">
                    <Typography variant="subtitle1" component="p">
                      {formatCurrency(fee.amountDue ?? 0)}
                    </Typography>

                    <InstalmentDetail instalment={firstInstallment} />
                    <Collapse in={showDetails}>
                      <Stack alignItems="flex-end">
                        {restInstallments?.map((installment) => (
                          <InstalmentDetail
                            key={installment.installment}
                            instalment={installment}
                          />
                        ))}
                        {serviceFee > 0 && (
                          <Typography
                            variant="body2"
                            component="span"
                            color="text.secondary"
                          >
                            {t('fees:serviceFee')} {formatCurrency(serviceFee)}
                          </Typography>
                        )}
                      </Stack>
                    </Collapse>
                    <Button
                      variant="text"
                      sx={{
                        py: 0,
                        px: 0.5,
                        mr: -0.5,
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                      disableTouchRipple
                      onClick={() =>
                        setShowInstallments((state) => {
                          state[index] = !state[index];
                          return [...state];
                        })
                      }
                    >
                      {showDetails
                        ? t('common:hideDetails')
                        : t('common:actions.viewDetails')}
                    </Button>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      {hasOtherFees && (
        <Stack spacing={2}>
          <Typography variant="subtitle1" color="slate.500">
            {t('fees:ineligibleFees')}
          </Typography>
          <Stack
            spacing={2}
            sx={{
              backgroundColor: 'slate.100',
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant="subtitle1"
                color="slate.500"
                id={`${descriptionId}-ineligible`}
              >
                {t('common:description')}
              </Typography>
              <Typography
                variant="subtitle1"
                color="slate.500"
                id={`${totalId}-ineligible`}
              >
                {t('common:total')}
              </Typography>
            </Stack>
            <Stack spacing={2}>
              {otherFees.map((fee) => {
                const { feeName, person, id } = fee;

                return (
                  <Stack
                    key={`${id.feeId}-${id.studentPartyId}`}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <PaymentName
                      feeName={feeName}
                      person={person}
                      aria-labelledby={`${descriptionId}-ineligible`}
                    />

                    <Stack
                      alignItems="flex-end"
                      spacing={1}
                      aria-labelledby={`${totalId}-ineligible`}
                    >
                      <Typography variant="subtitle1" component="p">
                        {formatCurrency(fee.amountDue ?? 0)}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      )}
      <Stack
        spacing={2}
        sx={{
          backgroundColor: 'slate.100',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Stack alignItems="flex-end">
          <Typography variant="subtitle1">
            {t('fees:todaysTotal')} {formatCurrency(todaysTotal)}
          </Typography>
          <Typography variant="body1">
            {t('fees:todaysServiceFee')} {formatCurrency(todaysServiceCharges)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
